/**
 * "Top 5 din noiembrie": members send ideas for the app all through November,
 * the five best are built in December.
 *
 * The Worker owns the rules (`workers/api/src/handlers/ideas.ts`). This copy of
 * the dates only decides when the trophy shows on Home, so that Home never has
 * to ask the server.
 */
export const IDEAS_CAMPAIGN = {
  opensAt: '2026-10-31T22:00:00.000Z',
  /** 23:59 on November 30, Bucharest. */
  closesAt: '2026-11-30T21:59:59.999Z',
  /** The trophy stays through December, while the five are being built. */
  showUntil: '2026-12-31T21:59:59.999Z',
} as const

const DAY_MS = 86_400_000

export function trophyVisible(now: Date = new Date()): boolean {
  const t = now.getTime()
  return (
    t >= new Date(IDEAS_CAMPAIGN.opensAt).getTime() &&
    t <= new Date(IDEAS_CAMPAIGN.showUntil).getTime()
  )
}

/** Ideas are still being taken (feedback always is). */
export function ideasOpen(now: Date = new Date()): boolean {
  return now.getTime() <= new Date(IDEAS_CAMPAIGN.closesAt).getTime()
}

/** Whole days left before ideas close; 0 on the last day. */
export function daysLeft(closesAt: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((new Date(closesAt).getTime() - now.getTime()) / DAY_MS))
}

/** How far through the month the campaign is, 0 to 1, for the progress rail. */
export function campaignProgress(closesAt: string, now: Date = new Date()): number {
  const start = new Date(IDEAS_CAMPAIGN.opensAt).getTime()
  const end = new Date(closesAt).getTime()
  return Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)))
}

// Per-device memory of two small things: whether this member has opened the
// page (the trophy's first dot) and whether they dismissed the tip on Home.
// Losing it only means seeing the dot or the tip once more.
const VISITED_KEY = 'ideas-2026-11-visited'
const TIP_KEY = 'ideas-2026-11-tip'

function read(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}
function write(key: string) {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // Private mode or blocked storage: nothing to remember, nothing breaks.
  }
}

export const hasVisitedIdeas = () => read(VISITED_KEY)
export const markIdeasVisited = () => write(VISITED_KEY)
export const tipDismissed = () => read(TIP_KEY)
export const dismissTip = () => write(TIP_KEY)
